import type { PlantRef, WorkCenter } from '@/types/api'

/**
 * What the analytics page reads from its URL, and how a tile says which way
 * it moved.
 *
 * Pure, so the rules can be tested without rendering a chart: which tab a link
 * opens, what an unknown value falls back to, and whether a fall is good news.
 */

// ---------------------------------------------------------------------------
// Tabs
// ---------------------------------------------------------------------------

/**
 * The four sections, in the order of the tab bar.
 *
 * Bosnian words because they are what the address bar shows — the same choice
 * as `/prijave`, `/zadaci` and `?osoba=`.
 */
export const ANALYTICS_TABS = ['pregled', 'prijave', '5s', 'radnici'] as const

export type AnalyticsTab = (typeof ANALYTICS_TABS)[number]

/**
 * The tab a URL opens on.
 *
 * `available` is what this customer has — no 5S tab without the 5S module —
 * so a link to a section that is not there lands on the first one rather than
 * on an empty panel.
 *
 * The search palette's person link predates the tabs (`?osoba=Amra#radnici`)
 * and is honoured as it is: the workforce table lives on the Radnici tab now,
 * and a link that used to scroll down to it must still find it.
 */
export function tabFromUrl(
  params: URLSearchParams,
  hash: string,
  available: readonly AnalyticsTab[],
): AnalyticsTab {
  const asked = params.get('tab')

  if (asked && (available as readonly string[]).includes(asked)) return asked as AnalyticsTab

  if ((params.has('osoba') || hash === '#radnici') && available.includes('radnici')) {
    return 'radnici'
  }

  return available[0] ?? 'pregled'
}

// ---------------------------------------------------------------------------
// Period
// ---------------------------------------------------------------------------

/** Days back from today. Twelve months is the default, as it always was. */
export const PERIODS = ['30', '90', '365'] as const

export type Period = (typeof PERIODS)[number]

export const DEFAULT_PERIOD: Period = '365'

export function periodFromUrl(params: URLSearchParams): Period {
  const asked = params.get('period')

  return asked && (PERIODS as readonly string[]).includes(asked) ? (asked as Period) : DEFAULT_PERIOD
}

/**
 * The same URL with one value changed.
 *
 * A value equal to its default is removed rather than written, so the page
 * opened from the sidebar and the page with every control at its default are
 * one address — and a bookmark carries only what somebody actually chose.
 */
export function withParam(
  params: URLSearchParams,
  key: string,
  value: string | null | undefined,
  fallback?: string,
): URLSearchParams {
  const next = new URLSearchParams(params)

  if (value === null || value === undefined || value === '' || value === fallback) {
    next.delete(key)
  } else {
    next.set(key, value)
  }

  return next
}

// ---------------------------------------------------------------------------
// Plants
// ---------------------------------------------------------------------------

/**
 * The plants somebody can narrow the page to, by name.
 *
 * Read off the machines they can see rather than off a list of plants: every
 * role can read `/work-centers`, it is already in the cache on most screens,
 * and a plant with no machine has nothing to chart. The server checks the
 * choice again — a plant the reader cannot see is refused, not ignored.
 */
export function plantsOf(centres: Pick<WorkCenter, 'plant' | 'plantId'>[] | undefined): Pick<PlantRef, 'id' | 'name'>[] {
  const plants = new Map<string, string>()

  for (const centre of centres ?? []) {
    if (centre.plant) plants.set(centre.plant.id, centre.plant.name)
  }

  return [...plants]
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name))
}

// ---------------------------------------------------------------------------
// Deltas
// ---------------------------------------------------------------------------

/**
 * Which way is good for a tile.
 *
 * `neither` is for the counts where more is not better or worse in itself —
 * more reports filed can be a worse month or a braver one.
 */
export type Better = 'up' | 'down' | 'neither'

export interface KpiDelta {
  direction: 'up' | 'down' | 'flat'
  /** current − previous, to one decimal: what the tile prints beside the arrow. */
  diff: number
  tone: 'good' | 'bad' | 'neutral'
}

/**
 * How a figure moved since the previous period.
 *
 * The difference itself rather than a percentage. On the numbers a plant has —
 * 3 late reports becoming 5 — a percentage reads "+67%" and sounds like an
 * emergency, while "+2" is the fact somebody can act on; and a percentage of a
 * percentage (5S, acceptance) is a figure nobody can read at all.
 *
 * Null when either side is missing: no comparison is better than one against
 * a nought that only means "nothing was measured".
 */
export function kpiDelta(
  current: number | null | undefined,
  previous: number | null | undefined,
  better: Better,
): KpiDelta | null {
  if (current === null || current === undefined || previous === null || previous === undefined) {
    return null
  }

  // Rounded before comparing, so 74.5 against 74.46 is "no change" rather
  // than an arrow over a difference the tile cannot print.
  const diff = Math.round((current - previous) * 10) / 10

  if (diff === 0) return { direction: 'flat', diff: 0, tone: 'neutral' }

  const direction = diff > 0 ? 'up' : 'down'
  const tone = better === 'neither' ? 'neutral' : direction === better ? 'good' : 'bad'

  return { direction, diff, tone }
}
